import { useTranslation } from "react-i18next";
import { useProfileForm } from "../hooks/useProfileForm";
import { useProfile } from "../services/GetProfile";
import Spinner from "@components/common/Spinner";
import ProfileActionButtons from "./ProfileActionButtons";
import ProfileDisplay from "./ProfileDisplay";
import ProfileFormFields from "./ProfileFormFields";
import ProfilePicture from "./ProfilePicture";
import SignatureUpload from "./SignatureUpload";
import type { ProfileModalTypes } from "../types";
import { getFullName } from "../utils/getFullName";
import CommonModal from "@components/common/Modal/Modal";
import Button from "@components/common/Button/Button";

const ProfileModal: React.FC<ProfileModalTypes> = ({ HideProfile }) => {
	const { t } = useTranslation();
	const { data, isLoading } = useProfile();
	const {
		isEditMode,
		isUpdating,
		isUploading,
		isUploadingSignature,
		uploadedSignatureKey,
		pendingSignatureDataUrl,
		form,
		handleImageChange,
		handleSignatureChange,
		onSubmit,
		handleCancel,
		handleEdit,
		showEmailChangeWarning,
		handleEmailChangeConfirm,
		handleEmailChangeCancel,
	} = useProfileForm(data, t, HideProfile);

	const { control, handleSubmit } = form;

	const fullName = getFullName({ firstName: data?.firstName, lastName: data?.lastName }, t);

	// Handle form validation errors
	const onFormError = (errors: Record<string, unknown>) => {
		console.log("=== FORM VALIDATION ERRORS ===", errors);
	};

	if (isLoading) return <Spinner overlay />;

	return (
		<div className="flex flex-1 flex-col">
			<form onSubmit={handleSubmit(onSubmit, onFormError)} className="flex  flex-1 flex-col">
				<div className="px-6 pb-6">
					<div className="flex flex-col items-center mb-8">
						<ProfilePicture
							profilePicture={data?.profilePicture}
							isUploading={isUploading}
							onImageChange={handleImageChange}
						/>
						<h3 className="text-xl font-semibold text-gray-900 text-center">{fullName}</h3>
					</div>

					<div className="space-y-6 max-w-lg mx-auto">
						{isEditMode ? (
							<ProfileFormFields control={control} t={t} />
						) : (
							<ProfileDisplay phoneNumber={data?.phoneNumber} emailAddress={data?.emailAddress} />
						)}

						{/* Signature Upload Section */}
						<SignatureUpload
							signature={data?.signature}
							uploadedSignatureKey={uploadedSignatureKey}
							isUploading={isUploadingSignature}
							isEditMode={isEditMode}
							onSignatureChange={handleSignatureChange}
							pendingSignatureDataUrl={pendingSignatureDataUrl}
							t={t}
						/>
					</div>
				</div>

				<ProfileActionButtons
					isEditMode={isEditMode}
					isUpdating={isUpdating}
					onEdit={handleEdit}
					onCancel={handleCancel}
					t={t}
				/>
			</form>

			<CommonModal
				isOpen={showEmailChangeWarning}
				onClose={handleEmailChangeCancel}
				title={t("psdaCommon.profile.emailChangeWarningModal.modalTitle")}
				showCloseIcon
				width="max-w-lg"
				rounded="rounded-2xl"
				padding="p-6"
				footer={
					<div className="flex flex-wrap justify-end gap-3 border-t border-slate-200/90 pt-4">
						<Button
							onClick={handleEmailChangeCancel}
							variant="dangerTertiary"
							text={t("psdaCommon.common.buttons.cancel")}
						/>
						<Button
							onClick={handleEmailChangeConfirm}
							variant="dangerSecondary"
							text={t("psdaCommon.profile.emailChangeWarningModal.confirmButton")}
						/>
					</div>
				}>
				<p className="text-base text-gray-700">{t("psdaCommon.profile.emailChangeWarningModal.modalMessage")}</p>
			</CommonModal>
		</div>
	);
};

export default ProfileModal;
